import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, Users, TrendingUp, DollarSign, Calendar, MapPin, LogOut } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminAuth } from '../services/adminAuth';

interface InsightData {
  totalSearches: number;
  totalOrders: number;
  conversionRate: number;
  totalRevenue: number;
  averageOrderValue: number;
  topStates: { state: string; count: number }[];
  topCities: { city: string; count: number }[];
  recentSearches: number;
  ordersByStatus: { status: string; count: number }[];
  searchesBySource: { source: string; count: number }[];
  monthlyTrends: { month: string; searches: number; orders: number }[];
}

const AdminInsights: React.FC = () => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState<InsightData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  // Check authentication on component mount
  useEffect(() => {
    if (!adminAuth.isAuthenticated()) {
      navigate('/admin-login');
      return;
    }
    fetchInsights();
  }, [navigate, timeRange]);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate date filter based on time range
      let dateFilter = '';
      if (timeRange !== 'all') {
        const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
        dateFilter = `created_at >= NOW() - INTERVAL '${days} days'`;
      } else {
        dateFilter = 'true';
      }

      // Fetch total searches
      const { data: searchesData, error: searchesError } = await supabase
        .from('property_searches')
        .select('*')
        .filter('created_at', 'gte', timeRange === 'all' ? '1970-01-01' : 
          new Date(Date.now() - (timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90) * 24 * 60 * 60 * 1000).toISOString());

      if (searchesError) throw searchesError;

      // Fetch orders (searches with completed orders)
      const ordersData = searchesData?.filter(search => 
        search.order_status === 'completed' || search.lumaprints_order_id
      ) || [];

      // Calculate metrics
      const totalSearches = searchesData?.length || 0;
      const totalOrders = ordersData.length;
      const conversionRate = totalSearches > 0 ? (totalOrders / totalSearches) * 100 : 0;
      const totalRevenue = ordersData.reduce((sum, order) => sum + (order.total_price || 0), 0);
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      // Top states
      const stateCounts = searchesData?.reduce((acc, search) => {
        const state = search.state || 'Unknown';
        acc[state] = (acc[state] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};
      
      const topStates = Object.entries(stateCounts)
        .map(([state, count]) => ({ state, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Top cities
      const cityCounts = searchesData?.reduce((acc, search) => {
        const city = search.city || 'Unknown';
        acc[city] = (acc[city] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};
      
      const topCities = Object.entries(cityCounts)
        .map(([city, count]) => ({ city, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Recent searches (last 24 hours)
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const recentSearches = searchesData?.filter(search => 
        new Date(search.created_at || '') > yesterday
      ).length || 0;

      // Orders by status
      const statusCounts = ordersData.reduce((acc, order) => {
        const status = order.order_status || 'pending';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      const ordersByStatus = Object.entries(statusCounts)
        .map(([status, count]) => ({ status, count }));

      // Searches by source
      const sourceCounts = searchesData?.reduce((acc, search) => {
        const source = search.image_source || 'address';
        acc[source] = (acc[source] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};
      
      const searchesBySource = Object.entries(sourceCounts)
        .map(([source, count]) => ({ source, count }));

      // Monthly trends (last 6 months)
      const monthlyData = searchesData?.reduce((acc, search) => {
        const month = new Date(search.created_at || '').toISOString().slice(0, 7);
        if (!acc[month]) {
          acc[month] = { searches: 0, orders: 0 };
        }
        acc[month].searches += 1;
        if (search.order_status === 'completed' || search.lumaprints_order_id) {
          acc[month].orders += 1;
        }
        return acc;
      }, {} as Record<string, { searches: number; orders: number }>) || {};

      const monthlyTrends = Object.entries(monthlyData)
        .map(([month, data]) => ({ month, ...data }))
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-6);

      setInsights({
        totalSearches,
        totalOrders,
        conversionRate,
        totalRevenue,
        averageOrderValue,
        topStates,
        topCities,
        recentSearches,
        ordersByStatus,
        searchesBySource,
        monthlyTrends
      });

    } catch (err) {
      console.error('Error fetching insights:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch insights');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    adminAuth.logout();
    navigate('/admin-login');
  };

  if (!adminAuth.isAuthenticated()) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading insights...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              Website Insights
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
            <button
              onClick={handleSignOut}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <LogOut className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Searches</p>
                <p className="text-2xl font-bold text-gray-900">{insights?.totalSearches || 0}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{insights?.totalOrders || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                <p className="text-2xl font-bold text-gray-900">{insights?.conversionRate.toFixed(1) || 0}%</p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">${insights?.totalRevenue.toFixed(2) || 0}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Recent Searches</p>
                <p className="text-2xl font-bold text-gray-900">{insights?.recentSearches || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Charts and Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top States */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Top States
            </h3>
            <div className="space-y-3">
              {insights?.topStates.map((state, index) => (
                <div key={state.state} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">#{index + 1}</span>
                    <span className="text-sm text-gray-900">{state.state}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{state.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Cities */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Top Cities
            </h3>
            <div className="space-y-3">
              {insights?.topCities.map((city, index) => (
                <div key={city.city} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">#{index + 1}</span>
                    <span className="text-sm text-gray-900">{city.city}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{city.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Order Status Breakdown */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Status</h3>
            <div className="space-y-3">
              {insights?.ordersByStatus.map((status) => (
                <div key={status.status} className="flex items-center justify-between">
                  <span className="text-sm text-gray-900 capitalize">{status.status}</span>
                  <span className="text-sm font-semibold text-gray-900">{status.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Search Sources */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Search Sources</h3>
            <div className="space-y-3">
              {insights?.searchesBySource.map((source) => (
                <div key={source.source} className="flex items-center justify-between">
                  <span className="text-sm text-gray-900 capitalize">{source.source}</span>
                  <span className="text-sm font-semibold text-gray-900">{source.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Monthly Trends */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Trends</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Searches
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orders
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversion Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {insights?.monthlyTrends.map((trend) => (
                  <tr key={trend.month}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {new Date(trend.month + '-01').toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long' 
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {trend.searches}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {trend.orders}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {trend.searches > 0 ? ((trend.orders / trend.searches) * 100).toFixed(1) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInsights; 